// CRM B-II: EIN Compose-Fenster, vier Einstiegspunkte.
//
// Firmenseite, Kontakt-Ansicht, Antworten aus dem Activity Feed, Work Session — der
// Unterschied zwischen ihnen ist ausschließlich der mitgegebene Kontext, nicht die
// Mechanik. Vier eigene Fenster wären vier Stellen, an denen Signatur, Warnung oder
// die Task-Verknüpfung später nur an dreien gepflegt werden.
//
// Gesendet wird über `lineupdashboardApiCrmMail`. Das Absenderpostfach kommt dort aus
// dem Cognito-Token und NICHT aus diesem Request — sonst könnte jeder als jeder
// senden. Diese Komponente kann den Absender also gar nicht wählen, und das ist so
// gewollt — sie ZEIGT ihn aber, damit niemand raten muss, aus welchem Postfach die
// Mail gleich rausgeht.
//
// Die Mail geht als **HTML** raus (`isHtml: true`), weil die Signatur HTML ist: eine
// Outlook-Signatur ist eine Tabelle mit Farben und Links, und als Text ist sie weg.
// Der Textkoerper ist deshalb ein `contenteditable` und kein Textfeld — Fett, Kursiv,
// Ueberschriften, Links, Bilder und Tabellen entstehen ueber `document.execCommand`,
// wie im Signatur-Editor auf der Kontoseite. Zusaetzlich geht der Klartext als
// `bodyText` mit, damit im Feld namens „Text" kein rohes HTML landet.
//
// ponytail: natives contenteditable statt einer Editor-Bibliothek — `execCommand` ist
// abgekuendigt und trotzdem der einzige Weg, der in allen Browsern an der
// Cursorposition arbeitet UND den Rueckgaengig-Verlauf behaelt. Ceiling: Aufzaehlungen,
// Farben, Zeilen in einer Tabelle nachtraeglich einfuegen — dafuer ist eine Bibliothek
// faellig.
import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Dropdown,
  Form,
  Modal,
  Spinner as BsSpinner,
} from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";

import { callLambda } from "../helpers/api/gateway";
import { linkTaskActivity } from "../helpers/activities";
import { taskObjectIds, taskRequestIds } from "../helpers/tasks";
import { client } from "../helpers/amplifyClient";
import {
  emailTemplatesByCreatedBy,
  emailTemplatesBySharedBase,
} from "../pages/profile/graphql/emailTemplates.queries";
import SignaturePreview from "./SignaturePreview";

import { buildDocument } from "./EmailFullBody";
import EmailFormatToolbar, { indentOnTab } from "./EmailFormatToolbar";
import {
  htmlToPlain,
  plainLetters,
  plainToHtml,
  sanitizeHtml,
  stripFonts,
} from "./emailHtml";

import {
  gapFor,
  gapsIn,
  placeholderValues,
  resolvePlaceholders,
} from "./emailPlaceholders";
import { generateEmailDraft, normalizeLanguage } from "../helpers/generateEmailDraft";
import i18n from "../i18n";

// D-III: die Grenze, die tatsaechlich bindet, ist NICHT die von Exchange, sondern die
// des REST-Gateways: API Gateway nimmt hoechstens 10 MB Nutzlast an, und Base64 blaeht
// die Datei um ein Drittel auf. 6 MB roh landen damit als ~8 MB im Koerper und passen.
//
// ponytail: eine Zahl statt eines zweiten Uebertragungswegs. Ceiling: wer groessere
// Anhaenge braucht, laedt die Datei ueber `lineupdashboardApiPresignedUrl` nach S3 und
// laesst das Lambda sie von dort holen — dann gilt wieder die Exchange-Grenze.
const MAX_ATTACHMENT_BYTES = 6 * 1024 * 1024;

// Erste Zeile einer Vorlage als Vorschau — damit in der Liste steht, WAS drin ist,
// und nicht nur, wie sie heisst.
const previewOf = (body, limit = 90) => {
  const text = String(body ?? "").replace(/\s+/g, " ").trim();
  return text.length > limit ? `${text.slice(0, limit)}…` : text;
};

const formatBytes = (bytes) =>
  bytes >= 1024 * 1024
    ? `${(bytes / 1024 / 1024).toFixed(1)} MB`
    : `${Math.round(bytes / 1024)} KB`;

const readDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(new Error(`${file.name} ist nicht lesbar.`));
    reader.readAsDataURL(file);
  });

// Datei -> Base64 ohne den data:-Praefix. Graph erwartet `contentBytes` genau so.
const toBase64 = async (file) => (await readDataUrl(file)).split(",")[1] ?? "";

// Getrennt wird an Komma, Semikolon UND Leerraum: in einer Adresse kann kein
// Leerzeichen stehen, also ist es ein Trenner wie jeder andere. Damit zerfaellt auch
// eine EINGEFUEGTE Liste ("a@b.de c@d.de") korrekt — Einfuegen loest kein keydown aus
// und laeuft nur hier durch.
export const addressesFrom = (value) =>
  value
    .split(/[,;\s]/)
    .map((part) => part.trim())
    .filter(Boolean);

// Absichtlich grob: eine Adresse mit @ und Punkt dahinter. Strenger zu pruefen hiesse,
// gueltige Adressen abzulehnen — die endgueltige Ablehnung kommt ohnehin von Exchange.
// Hier geht es nur darum, den Tippfehler VOR dem Senden sichtbar zu machen.
export const isEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

// C-II, zweiter Weg: Bausteine und Platzhalter direkt beim Schreiben, ohne die Hand
// von der Tastatur zu nehmen — `/` sucht Bausteine, `<` sucht Platzhalter. Ausgeloest
// wird nur am Wortanfang: mitten in "und/oder" oder "a<b" soll nichts aufklappen.
const TRIGGER = /(^|\s)([/<])([^\s/<]*)$/;

/**
 * @returns { trigger, query, start } oder null — `start` ist die Position des
 * Auslosezeichens, damit beim Einfuegen genau `/text` bzw. `<text` ersetzt wird.
 */
export const triggerAt = (text, caret) => {
  const match = String(text ?? "").slice(0, caret).match(TRIGGER);
  if (!match) return null;
  return {
    trigger: match[2],
    query: match[3],
    start: caret - match[3].length - 1,
  };
};

/**
 * Adressfeld wie im Mailprogramm: jede Adresse ein Plaettchen mit x, Eingabe per
 * Enter, Komma oder Semikolon, Rueckschritt im leeren Feld nimmt die letzte weg.
 * Eine unplausible Adresse wird rot — sie laesst sich trotzdem stehen lassen, aber der
 * Senden-Knopf bleibt gesperrt.
 *
 * Der Entwurfstext liegt ABSICHTLICH beim Aufrufer und nicht hier: wer eine Adresse
 * tippt und direkt auf Senden klickt, hat noch kein Plaettchen — ohne das darf die
 * Adresse nicht verloren gehen.
 *
 * ponytail: kein Tag-Widget als Abhaengigkeit. Ceiling ist die fehlende
 * Vervollstaendigung waehrend des Tippens; wer die braucht, tauscht das Feld gegen
 * react-selects CreatableSelect — die Chips sind dann geschenkt.
 */
const AddressField = (props) => {
  const { values, draft, onChange, onDraftChange, placeholder, id } = props;
  const { t } = useTranslation(["components", "common"]);

  const commit = () => {
    const next = addressesFrom(draft).filter((address) => !values.includes(address));
    if (next.length) onChange([...values, ...next]);
    onDraftChange("");
  };

  const keyDown = (event) => {
    // Leerzeichen bestaetigt wie Enter — in einer Adresse kann keines stehen. Das
    // verhindert zugleich Leerraum am Ende des Entwurfs.
    if (
      event.key === "Enter" ||
      event.key === "," ||
      event.key === ";" ||
      event.key === " "
    ) {
      event.preventDefault();
      if (draft.trim()) commit();
      return;
    }
    // Tab soll weiterspringen — aber nicht den halb getippten Namen verlieren.
    if (event.key === "Tab" && draft.trim()) {
      commit();
      return;
    }
    if (event.key === "Backspace" && !draft && values.length) {
      onChange(values.slice(0, -1));
    }
  };

  return (
    <div
      className="d-flex flex-wrap align-items-center gap-1 py-1 flex-grow-1"
      // Klick auf die Leerfläche landet im Eingabefeld, wie im Mailprogramm.
      onClick={(event) => {
        if (event.target === event.currentTarget) {
          document.getElementById(id)?.focus();
        }
      }}
    >
      {values.map((address) => {
        const bad = !isEmail(address);
        return (
          <span
            key={address}
            className={`badge rounded-pill fw-normal d-inline-flex align-items-center gap-1 ${
              bad
                ? "bg-danger-subtle text-danger-emphasis border border-danger"
                : "bg-light text-dark border"
            }`}
            title={bad ? t("Not a valid e-mail address.") : address}
          >
            {bad && <i className="mdi mdi-alert-circle-outline" />}
            {address}
            <i
              className="mdi mdi-close"
              role="button"
              tabIndex={0}
              aria-label={t("Remove")}
              onClick={() => onChange(values.filter((item) => item !== address))}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  onChange(values.filter((item) => item !== address));
                }
              }}
            />
          </span>
        );
      })}
      <input
        id={id}
        type="text"
        className="border-0 flex-grow-1 bg-transparent"
        style={{ minWidth: 140, outline: "none" }}
        value={draft}
        placeholder={values.length ? "" : placeholder}
        onChange={(event) => onDraftChange(event.target.value)}
        onKeyDown={keyDown}
        onBlur={() => draft.trim() && commit()}
      />
    </div>
  );
};

/**
 * #4264: ob und wie der Verlauf unter der Antwort mitkommt — die Antwort von
 * `prepareReply`. Grün: Outlook-Unterhaltung mit Zitat. Gelb: Zitat ja, aber die
 * Unterhaltung bricht (oder nur der gespeicherte Text). Rot: kein Verlauf.
 */
const ReplyHistoryNotice = ({ plan, showQuote, onToggleQuote }) => {
  const { t } = useTranslation(["components", "common"]);

  let variant = "warning";
  let icon = "mdi-alert-outline";
  let text;
  if (plan.status === "loading") {
    variant = null;
    text = t("Checking whether the conversation history can be included…");
  } else if (plan.status === "error") {
    text = t(
      "Could not check whether the conversation history can be included. If it cannot, you will be asked before anything is sent."
    );
  } else if (plan.quoteMode === "thread") {
    variant = null;
    icon = "mdi-check";
    text = t("The conversation history is quoted below your text.");
  } else if (plan.quoteMode === "quote") {
    text = {
      otherMailbox: t(
        "The conversation history is quoted below your text. The original e-mail is in the mailbox of {{mailbox}}, so the reply is sent from your mailbox and does not continue the Outlook conversation.",
        { mailbox: plan.mailboxUpn }
      ),
      notInMailbox: t(
        "The conversation history is quoted below your text from the copy saved in the dashboard (without formatting). The original e-mail is no longer in the mailbox, so the reply does not continue the Outlook conversation."
      ),
      unreachable: t(
        "The conversation history is quoted below your text from the copy saved in the dashboard (without formatting). The original e-mail could not be read from the mailbox, so the reply does not continue the Outlook conversation."
      ),
      migrated: t(
        "The conversation history is quoted below your text from the copy saved in the dashboard (without formatting). The reply does not continue the Outlook conversation."
      ),
      threadFailed: t(
        "Outlook could not create the reply. The conversation history can still be quoted below your text, but the reply does not continue the Outlook conversation."
      ),
    }[plan.reason] ||
      t(
        "The conversation history is quoted below your text, but the reply does not continue the Outlook conversation."
      );
  } else {
    variant = "danger";
    icon = "mdi-alert-circle-outline";
    text = t(
      "This reply is sent without the conversation history: the original e-mail is no longer available."
    );
  }

  return (
    <div className="px-3 pb-2">
      <div
        className={`small d-flex align-items-start gap-1 ${
          variant ? `alert alert-${variant} py-2 mb-0` : "text-muted"
        }`}
      >
        {plan.status === "loading" ? (
          <BsSpinner size="sm" animation="border" className="me-1 flex-shrink-0" />
        ) : (
          <i className={`mdi ${icon} flex-shrink-0`} />
        )}
        <span className="flex-grow-1">{text}</span>
        {plan.quoteHtml && (
          <Button
            variant="link"
            size="sm"
            className="p-0 small text-nowrap"
            onClick={onToggleQuote}
          >
            {showQuote ? t("Hide quote") : t("Show quote")}
          </Button>
        )}
      </div>
      {/* Fremdes HTML: dieselben zwei Schichten wie in der Leseansicht (EmailFullBody) —
          DOMPurify und ein abgeschotteter iframe ohne Fernbilder. */}
      {showQuote && plan.quoteHtml && (
        <iframe
          title={t("Quote")}
          sandbox=""
          srcDoc={buildDocument(sanitizeHtml(plan.quoteHtml), false, "")}
          className="border rounded mt-2"
          style={{ width: "100%", height: 320, display: "block" }}
        />
      )}
    </div>
  );
};

/**
 * Der EINZIGE Weg, auf dem eine Adresse ungefragt im Empfängerfeld landet: die Antwort
 * geht an den Absender, und eine Aufgabe mit Ansprechpartner an dessen Adresse. Eine
 * Aufgabe OHNE Ansprechpartner belegt nichts vor — die Firmenkontakte stehen darunter
 * als Schnellauswahl, das sind Knöpfe und keine Empfänger.
 *
 * `contactPersonId` muss dafür wahr sein: ohne diese Bedingung träfe ein `undefined`
 * jeden Kontakt, dessen `id` fehlt.
 *
 * Antwort auf die EIGENE gesendete Mail: wie in Outlook an deren Empfaenger, nicht an
 * den Absender — das waeren wir selbst, und die Filterkette verwirft eine rein interne
 * Mail ohne Datensatz (sie fehlte im Verlauf).
 */
export const seedRecipient = ({ replyTo, contacts = [], contactPersonId }) => {
  if (replyTo?.direction === "outbound") return (replyTo.toEmails || []).join(", ");
  if (replyTo?.fromEmail) return replyTo.fromEmail;
  if (!contactPersonId) return "";
  return contacts.find((contact) => contact.id === contactPersonId)?.email || "";
};

/**
 * @param company        { id, name, doNotContact } — Kontext der Mail
 * @param contacts       Firmenkontakte zur Auswahl
 * @param contactPersonId  vorbelegter Empfänger, optional
 * @param replyTo        vorhandene Mail, auf die geantwortet wird (Feed) — optional
 * @param task           Aufgabe, aus der gesendet wird — optional
 * @param objectId       Vorgang, aus dessen Verlauf gesendet wird (Kundenanfrage) — optional
 * @param objectType     Typ dazu, Standard "CustomerRequest" — optional
 * @param defaultSubject Betreff aus dem Kontext, editierbar
 * @param defaultBody    vorbelegter Text als Klartext, editierbar — optional
 * @param onSent         bekommt den angelegten `Emails`-Datensatz, oder null
 * @param onClose        schließt das Fenster
 */
const EmailComposeModal = (props) => {
  const {
    company,
    contacts = [],
    contactPersonId,
    replyTo,
    task,
    defaultSubject,
    // Gesetzt, wo das Fenster ueber den Plus-Knopf entstand: schliesst es und
    // oeffnet die Firmenauswahl, um die Mail an eine andere Firma zu beginnen.
    onChangeCompany,
    onSent,
    onClose,
  } = props;
  const { t } = useTranslation(["components", "common"]);
  const { user } = useSelector((state) => ({ user: state.Auth.user }));

  const initialTo = useMemo(
    () => seedRecipient({ replyTo, contacts, contactPersonId }),
    [replyTo, contacts, contactPersonId]
  );

  // Adressen als Liste, plus der noch nicht bestaetigte Entwurfstext je Feld.
  const [to, setTo] = useState(() => addressesFrom(initialTo));
  const [toDraft, setToDraft] = useState("");
  // Cc der eigenen gesendeten Mail mitnehmen (siehe seedRecipient).
  const replyCc = replyTo?.direction === "outbound" ? replyTo.ccEmails || [] : [];
  const [cc, setCc] = useState(replyCc);
  const [ccDraft, setCcDraft] = useState("");
  const [bcc, setBcc] = useState([]);
  const [bccDraft, setBccDraft] = useState("");
  // Cc und Bcc sind im Regelfall leer und kosten trotzdem zwei Zeilen Hoehe. Sie
  // erscheinen auf Knopfdruck und verschwinden nicht wieder — wer sie geoeffnet hat,
  // will sie sehen, auch wenn er die Adresse gerade wieder herausgenommen hat.
  const [showCc, setShowCc] = useState(replyCc.length > 0);
  const [showBcc, setShowBcc] = useState(false);
  const [subject, setSubject] = useState(
    replyTo?.subject
      ? `Re: ${String(replyTo.subject).replace(/^\s*(re|aw|antw)\s*:\s*/i, "")}`
      : defaultSubject || task?.subject || ""
  );
  // `defaultBody` ist Klartext aus dem Aufrufer (z. B. die offenen Punkte einer
  // Checkliste) — im Editor lebt HTML, also einmal umgesetzt.
  const [body, setBody] = useState(() =>
    props.defaultBody ? plainToHtml(props.defaultBody) : ""
  );
  const [signatures, setSignatures] = useState([]);
  const [signatureId, setSignatureId] = useState("");
  const [sending, setSending] = useState(false);
  // #4264: kommt der Verlauf mit, und wie? Das Lambda prueft es beim Oeffnen
  // (`prepareReply`), damit der Nutzer es VOR dem Senden sieht. Beim Senden geht der
  // Modus mit, und das Lambda nimmt genau ihn — oder sendet nicht und meldet, was noch
  // geht (409). Dann steht hier der neue Modus, und der naechste Klick auf Senden ist die
  // Bestaetigung.
  // { status: "loading" | "ready" | "error", quoteMode, reason, mailboxUpn, quoteHtml }
  const [replyPlan, setReplyPlan] = useState(() =>
    replyTo ? { status: "loading" } : null
  );
  const [showQuote, setShowQuote] = useState(false);
  // Das Gateway gibt nach 29 s auf (504), das Lambda laeuft bis zu 120 s weiter und
  // sendet trotzdem. "Fehlgeschlagen" waere dann gelogen und verleitet zum zweiten Klick —
  // der Kunde bekaeme die Mail doppelt. Stattdessen: Status unklar, erst nachsehen.
  const [sendUncertain, setSendUncertain] = useState(false);
  const replyEmailId = replyTo ? replyTo.rawId || replyTo.id : null;
  useEffect(() => {
    if (!replyEmailId) return undefined;
    let cancelled = false;
    callLambda("lineupdashboardApiCrmMail", {
      body: { action: "prepareReply", emailId: replyEmailId },
    })
      .then((result) => {
        if (!cancelled) setReplyPlan({ status: "ready", ...result });
      })
      .catch((error) => {
        console.warn("prepareReply fehlgeschlagen", error);
        if (!cancelled) setReplyPlan({ status: "error" });
      });
    return () => {
      cancelled = true;
    };
  }, [replyEmailId]);
  // C-II: Vorlagen und Bausteine derselben Abfrage wie die Signaturen — ein Modell,
  // drei Typen (C-I).
  const [templates, setTemplates] = useState([]);
  // D-III: Dateien liegen nur hier, gespeichert wird nichts. Angezeigt werden sie
  // spaeter live aus dem Postfach (B-IV).
  const [files, setFiles] = useState([]);
  // Der Textkoerper: `contenteditable`. Das DOM ist die Wahrheit, `body` sein Abbild
  // fuer den Versand — innerHTML bei jedem Tastendruck neu zu SETZEN schiebt den Cursor
  // an den Anfang, der klassische contenteditable-Fehler.
  const bodyRef = React.useRef(null);
  // Das contenteditable startet leer: den vorbelegten Text einmal ins DOM schreiben.
  // Nur beim Oeffnen — spaeter wuerde es den Cursor an den Anfang schieben.
  useEffect(() => {
    if (body && bodyRef.current) bodyRef.current.innerHTML = body;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // Verstecktes Dateifeld hinter dem Klammersymbol — wie im Mailprogramm.
  const fileRef = React.useRef(null);
  // Zweites, getrenntes Feld fuer Bilder IM Text: ein Bild ist kein Anhang.
  const imageRef = React.useRef(null);
  // Auswahl im Textfeld: `/` sucht Bausteine, `<` sucht Platzhalter.
  // { trigger, query, start, index } — `start` ist die Position des Auslosezeichens,
  // damit beim Einfuegen genau `/text` bzw. `<text` ersetzt wird.
  const [menu, setMenu] = useState(null);

  // KI-Entwurf: eine Zeile Anweisung, der Rest kommt aus dem Kontext, den dieses
  // Fenster ohnehin haelt (Firma, Kontakt, Absender, Bezugsmail, Aufgabe).
  const [aiOpen, setAiOpen] = useState(false);
  const [aiPrompt, setAiPrompt] = useState("");
  const [aiRunning, setAiRunning] = useState(false);
  // Der Entwurf ERSETZT den Text — deshalb ein Weg zurueck. execCommand-Undo faellt
  // beim Setzen von innerHTML aus, sonst waere das der richtige Ort dafuer.
  const [aiUndo, setAiUndo] = useState(null);
  // C-I: die PRIMÄRE Signatur ist beim Öffnen ausgewählt, nicht unsichtbar beim Senden
  // angehängt — sie steht unter dem Text, sichtbar, austauschbar und ganz entfernbar.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!user?.id) return;
      try {
        // C-I: eigene UND global geteilte Vorlagen — wie auf der Kontoseite. Nur die
        // eigenen zu laden hiesse, dass geteilte Vorlagen dort sichtbar, im
        // Schreibfenster aber unbenutzbar sind. Eine gescheiterte Abfrage darf die
        // andere nicht mitnehmen.
        const [own, shared] = await Promise.allSettled([
          client.graphql({
            query: emailTemplatesByCreatedBy,
            variables: { createdBy: user.id },
          }),
          client.graphql({
            query: emailTemplatesBySharedBase,
            variables: { sharedBase: "shared" },
            skipGlobalError: true,
          }),
        ]);
        const ownItems =
          own.status === "fulfilled"
            ? own.value.data.emailTemplatesByCreatedBy?.items || []
            : [];
        const sharedItems =
          shared.status === "fulfilled"
            ? shared.value.data.emailTemplatesBySharedBase?.items || []
            : [];
        // Eine eigene Vorlage kann auch geteilt sein und steht dann in beiden Listen.
        const all = [
          ...ownItems,
          ...sharedItems.filter(
            (item) => !ownItems.some((mine) => mine.id === item.id)
          ),
        ];
        const items = all.filter((item) => item.type === "signature");
        if (cancelled) return;
        setSignatures(items);
        setTemplates(all.filter((item) => item.type !== "signature"));
        const primary = items.find((item) => item.isPrimary === "true");
        if (primary) setSignatureId(primary.id);
      } catch (error) {
        // Ohne Signatur lässt sich trotzdem senden — kein Grund, das Fenster zu
        // blockieren.
        console.log("Signaturen nicht lesbar", error);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  // Die Signatur liegt NICHT im Textfeld, sondern als eigener Block darunter. Vorher
  // wurde sie per Zeichenkettentausch in den Text hinein- und wieder herausgeschnitten —
  // das ging schief, sobald der Nutzer sie anfasste, und mit HTML gar nicht mehr.
  const activeSignature = signatures.find((item) => item.id === signatureId) || null;
  const signatureHtml = useMemo(
    () => (activeSignature ? sanitizeHtml(activeSignature.body) : ""),
    [activeSignature]
  );

  // C-II: Vorlage ersetzt Betreff und Text, Baustein wird an der CURSORPOSITION
  // eingefuegt. Aufgeloest wird beim EINFUEGEN — beim Speichern waere die Vorlage nach
  // einmaligem Gebrauch aufgebraucht.
  //
  // Kontakt auflösen: vorselektierte ID gewinnt; fehlt sie (z. B. Einstieg ueber "Neue
  // E-Mail" an der Firma), wird der Kontakt anhand der Empfaengeradresse ermittelt.
  // Der Entwurfstext zaehlt mit: sonst geht die getippte Adresse beim Klick auf
  // Senden verloren, weil noch kein Plaettchen daraus geworden ist.
  const toAddresses = [...to, ...addressesFrom(toDraft)];
  const ccAddresses = [...cc, ...addressesFrom(ccDraft)];
  const bccAddresses = [...bcc, ...addressesFrom(bccDraft)];
  const badAddresses = [...toAddresses, ...ccAddresses, ...bccAddresses].filter(
    (address) => !isEmail(address)
  );
  const activeContact =
    contacts.find((item) => item.id === contactPersonId) ||
    contacts.find((item) => item.email && toAddresses.includes(item.email)) ||
    null;

  const placeholderContext = {
    company,
    contact: activeContact,
    sender: user,
  };

  // Alles, was den Text veraendert, laeuft ueber diese eine Stelle: execCommand
  // braucht eine Auswahl IM Feld. Steht der Cursor woanders (Klick auf einen Knopf
  // hat den Fokus geholt), wird ans Ende gesetzt — der harmlose Fall.
  const exec = (command, value) => {
    const field = bodyRef.current;
    if (!field) return;
    const selection = window.getSelection();
    if (!selection?.anchorNode || !field.contains(selection.anchorNode)) {
      field.focus();
      const range = document.createRange();
      range.selectNodeContents(field);
      range.collapse(false);
      selection?.removeAllRanges();
      selection?.addRange(range);
    }
    document.execCommand(command, false, value);
    setBody(field.innerHTML);
  };

  const insertAtCursor = (text) => exec("insertHTML", plainToHtml(text));

  // Cursor als Textknoten plus Position darin — nur so laesst sich das getippte
  // `/text` beim Einfuegen wieder wegnehmen.
  const caretAt = () => {
    const selection = window.getSelection();
    if (!selection?.rangeCount) return null;
    const range = selection.getRangeAt(0);
    const node = range.startContainer;
    if (node.nodeType !== 3 || !bodyRef.current?.contains(node)) return null;
    return { node, offset: range.startOffset, rect: range.getBoundingClientRect() };
  };

  const openMenuFromCaret = () => {
    const caret = caretAt();
    const found = caret && triggerAt(caret.node.textContent, caret.offset);
    if (!found) {
      setMenu(null);
      return;
    }
    // Im contenteditable ist die echte Cursorposition ablesbar — das Menue steht
    // darunter und nicht irgendwo am Rand.
    const fallback = bodyRef.current.getBoundingClientRect();
    setMenu({
      ...found,
      index: 0,
      left: caret.rect.left || fallback.left + 12,
      top: (caret.rect.bottom || fallback.top + 24) + 4,
    });
  };

  // Beim Weitertippen nach dem Auslosezeichen wird gesucht — bei Bausteinen im Namen,
  // im Betreff UND im Text, weil man einen Baustein an seinem Inhalt erinnert und nicht
  // an seinem Namen.
  const menuOptions = useMemo(() => {
    if (!menu) return [];
    const query = menu.query.toLowerCase();
    if (menu.trigger === "/") {
      return templates
        .filter((item) => item.type === "snippet")
        .filter((item) =>
          `${item.name} ${item.subject || ""} ${item.body || ""}`
            .toLowerCase()
            .includes(query)
        )
        .slice(0, 8)
        .map((item) => ({
          key: item.id,
          label: item.name,
          hint: previewOf(item.body, 60),
          snippet: item,
        }));
    }
    // Ein Platzhalter ohne Wert bleibt waehlbar: eingefuegt wird dann die sichtbare
    // Luecke `[[name]]`, dieselbe, die auch eine Vorlage hinterlaesst — sonst waere
    // z.B. die Anrede (bei vier von fünf Kontakten ohne Geschlecht leer) hier nie zu
    // finden, obwohl sie oft von Hand nachgetragen werden soll.
    return Object.entries(placeholderValues(placeholderContext))
      .filter(([name]) => name.toLowerCase().includes(query))
      .map(([name, value]) => ({
        key: name,
        label: name,
        hint: value || t("no value in this e-mail — inserts a gap to fill in"),
        insert: value || gapFor(name),
        empty: !value,
      }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [menu, templates, company, activeContact, user, t]);

  // Gewaehlt wird der WERT, nicht der Platzhalter: die Auswahl loest ihn direkt auf.
  // Ein Baustein ist HTML (Fett, Links, ...) und wird als solches eingefuegt statt
  // durch `plainToHtml` zu Text verwandelt zu werden; ein geteilter Baustein kann von
  // einem anderen Nutzer stammen, deshalb hier noch einmal saniert.
  const applyChoice = (option) => {
    if (!menu || !option) return;
    const insert = option.snippet
      ? resolvePlaceholders(option.snippet.body, placeholderContext).text
      : option.insert ?? "";
    // Das getippte `/text` bzw. `<text` verschwindet mit: markieren, dann ersetzen.
    const caret = caretAt();
    if (caret && menu.start <= caret.offset) {
      const range = document.createRange();
      range.setStart(caret.node, menu.start);
      range.setEnd(caret.node, caret.offset);
      const selection = window.getSelection();
      selection.removeAllRanges();
      selection.addRange(range);
    }
    setMenu(null);
    if (option.snippet) exec("insertHTML", stripFonts(sanitizeHtml(insert)));
    else insertAtCursor(insert);
  };

  const menuKeyDown = (event) => {
    if (!menu) return;
    // Zuerst Escape: der Kasten steht auch ohne Treffer offen und muss sich
    // wegtippen lassen. Alles Weitere braucht eine Auswahl.
    if (event.key === "Escape") {
      event.preventDefault();
      setMenu(null);
      return;
    }
    if (!menuOptions.length) return;
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      const step = event.key === "ArrowDown" ? 1 : -1;
      setMenu((current) =>
        current
          ? {
              ...current,
              index:
                (current.index + step + menuOptions.length) % menuOptions.length,
            }
          : current
      );
      return;
    }
    if (event.key === "Enter" || event.key === "Tab") {
      event.preventDefault();
      applyChoice(menuOptions[menu.index]);
    }
  };

  const insertTemplate = (template) => {
    if (!template) return;
    const resolvedBody = resolvePlaceholders(template.body, placeholderContext);
    const resolvedSubject = resolvePlaceholders(template.subject, placeholderContext);

    if (template.type === "snippet") {
      exec("insertHTML", stripFonts(sanitizeHtml(resolvedBody.text)));
      return;
    }

    // Vorlage: Betreff und Text ersetzen. Die Signatur bleibt unberuehrt darunter
    // stehen — sie gehoert dem Absender, nicht der Vorlage.
    if (resolvedSubject.text.trim()) setSubject(resolvedSubject.text);
    // Hier darf innerHTML gesetzt werden: der Text wird ERSETZT, ein Cursor, der
    // dabei springt, springt an die richtige Stelle. Der Vorlagentext ist selbst
    // schon HTML (Fett, Links, ...), also kein `plainToHtml` — nur noch einmal
    // saniert, falls die Vorlage von einem anderen Nutzer geteilt wurde.
    const html = stripFonts(sanitizeHtml(resolvedBody.text));
    if (bodyRef.current) bodyRef.current.innerHTML = html;
    setBody(html);
  };

  const writeWithAi = async () => {
    if (!aiPrompt.trim() || aiRunning) return;
    setAiRunning(true);
    const previous = { subject, html: bodyRef.current?.innerHTML ?? "" };
    try {
      const draft = await generateEmailDraft({
        instruction: aiPrompt,
        values: placeholderValues(placeholderContext),
        // In welcher Sprache der Kunde angeschrieben wird, entscheidet der Kunde —
        // erst der Kontakt, dann die Firma, und nur wenn beides fehlt, die Sprache
        // des Dashboards.
        // Geprueft statt mit `||` gekettet: `" "` aus der Datenbank und `"fr-FR"` aus
        // der Spracherkennung sind wahr, aber keine Sprache (normalizeLanguage).
        language:
          normalizeLanguage(activeContact?.communicationLanguage) ||
          normalizeLanguage(company?.communicationLanguage) ||
          normalizeLanguage(i18n.language),
        subject,
        draft: htmlToPlain(previous.html),
        replyTo,
        task,
      });
      if (draft.subject) setSubject(draft.subject);
      const html = plainToHtml(draft.body);
      if (bodyRef.current) bodyRef.current.innerHTML = html;
      setBody(html);
      setAiUndo(previous);
    } catch (error) {
      console.log("EmailComposeModal AI:", error);
      toast.error(t("The draft could not be created."));
    } finally {
      setAiRunning(false);
    }
  };

  const undoAiDraft = () => {
    if (!aiUndo) return;
    setSubject(aiUndo.subject);
    if (bodyRef.current) bodyRef.current.innerHTML = aiUndo.html;
    setBody(aiUndo.html);
    setAiUndo(null);
  };

  // Ein Bild IM Text liegt als data:-URI im Koerper und wird im Lambda zum
  // cid-Anhang — es geht denselben Weg wie ein Anhang und muss in dieselbe Rechnung.
  // Ohne das laufen 5 MB Foto lautlos in die Ablehnung des Gateways.
  const inlineBytes = useMemo(() => {
    let sum = 0;
    for (const match of body.matchAll(/data:image\/[a-z+]+;base64,([^"'\s>]+)/gi)) {
      sum += Math.floor(match[1].length * 0.75);
    }
    return sum;
  }, [body]);

  // C-II, die Warnung: geprueft wird der Text, der DA STEHT — nicht der Zustand vom
  // Einfuegen. Wer eine Luecke von Hand fuellt, soll die Warnung verschwinden sehen;
  // wer eine Vorlage zweimal einfuegt, soll sie wieder bekommen.
  const openGaps = useMemo(
    () => gapsIn(`${subject}\n${htmlToPlain(body)}`),
    [subject, body]
  );

  // D-III Punkt 3: die Obergrenze wird VOR dem Senden angezeigt. Ohne sie faellt die
  // Ablehnung erst nach dem Klick, mit fertig getipptem Text.
  const totalBytes =
    files.reduce((sum, file) => sum + file.size, 0) + inlineBytes;
  const tooLarge = totalBytes > MAX_ATTACHMENT_BYTES;

  // Die FileList wird HIER materialisiert, nicht im State-Updater: der Aufrufer setzt
  // direkt nach dem Auswaehlen `input.value = ""`, und das leert die FileList. Der
  // Updater laeuft aber erst beim Rendern — er sah dann eine leere Liste, und der
  // Anhang tauchte gar nicht auf. Genau der Fehler, der "manchmal" passierte.
  const addFiles = (list) => {
    const picked = Array.from(list ?? []);
    // 0 Byte heisst in der Praxis: OneDrive-Platzhalter oder ein anderes Programm
    // haelt die Datei. Sie waere erst beim Senden durchgefallen.
    const usable = picked.filter((file) => file.size);
    const empty = picked.filter((file) => !file.size);
    if (empty.length) {
      toast.error(
        t("{{names}} could not be attached (empty or not readable).", {
          names: empty.map((file) => file.name).join(", "),
        })
      );
    }
    if (usable.length) setFiles((current) => [...current, ...usable]);
  };

  // A-VIII Stufe 2, hierher verschoben: eine gesperrte Firma WARNT, sie sperrt nicht.
  // Auf eine Anfrage zu antworten, die von der gesperrten Firma selbst kommt, muss
  // möglich bleiben — dieselbe Form wie am Anrufknopf. Fehlender Wert und "" zählen
  // wie "false": nur die 2.431 migrierten tragen überhaupt einen.
  const blocked = company?.doNotContact === "true";

  const send = async () => {
    const recipients = toAddresses;
    if (!recipients.length || tooLarge || badAddresses.length) return;
    // Doppelt gesichert: der Knopf ist gesperrt, und hier faellt es nochmal auf. Eine
    // Luecke beim Kunden ist der Fehler, der diese ganze Mechanik rechtfertigt.
    if (gapsIn(`${subject}\n${htmlToPlain(body)}`).length) return;
    setSending(true);
    // Nur waehrend der Sendeaufruf unterwegs ist, ist ein Abbruch "unklar". Ein Fehler
    // davor (Anhang nicht lesbar) hat sicher nichts gesendet.
    let inFlight = false;
    try {
      // D-III: die Bytes gehen mit dem Sendeaufruf mit; das Lambda haengt sie an den
      // Entwurf. Gespeichert wird hier nichts — angezeigt wird der Anhang spaeter live
      // aus dem Postfach (B-IV).
      const attachments = await Promise.all(
        files.map(async (file) => ({
          name: file.name,
          contentType: file.type || "application/octet-stream",
          size: file.size,
          contentBytes: await toBase64(file),
        }))
      );
      inFlight = true;
      const response = await callLambda("lineupdashboardApiCrmMail", {
        body: {
          action: replyTo ? "reply" : "send",
          ...(replyTo
            ? {
                emailId: replyEmailId,
                // Ohne Pruefergebnis nichts mitschicken: das Lambda erwartet dann den
                // besten Weg und fragt nach, falls der nicht geht.
                ...(replyPlan?.status === "ready" ? { quoteMode: replyPlan.quoteMode } : {}),
              }
            : {}),
          to: recipients,
          cc: ccAddresses,
          bcc: bccAddresses,
          subject,
          // Getippter Text als HTML, darunter die Signatur. `bodyText` traegt dieselbe
          // Mail als Klartext — der Sendepfad schreibt es an den `Emails`-Datensatz,
          // und dort gehoert kein HTML hin.
          // Der Koerper ist jetzt HTML aus dem Editor. Gefiltert wird VOR dem Senden:
          // eingefuegter Inhalt aus Word oder einer geteilten Vorlage hat hier im DOM
          // gelegen und darf nicht ungeprueft beim Kunden landen.
          body: signatureHtml
            ? `${sanitizeHtml(body)}<br><br>${signatureHtml}`
            : sanitizeHtml(body),
          bodyText: [htmlToPlain(body), htmlToPlain(signatureHtml)]
            .filter(Boolean)
            .join("\n\n"),
          isHtml: true,
          companyID: company?.id,
          contactPersonID: contactPersonId || activeContact?.id,
          taskID: task?.id,
          // Die Anfragen, an denen diese Mail AUSDRUECKLICH haengt: die der Aufgabe
          // (eine Aufgabe kann an mehreren haengen, `taskRequestIds`) und die, aus
          // deren Verlauf gesendet wurde. Das Lambda verknuepft sonst nur die OFFENEN
          // Anfragen der Firma (Status 10/20/30) — eine Mail aus dem Verlauf einer
          // bereits gewonnenen oder verlorenen Anfrage waere dort nie erschienen,
          // obwohl sie genau dort geschrieben wurde.
          objectIDs: [
            ...new Set([
              ...taskRequestIds(task),
              ...(props.objectId && (props.objectType || "CustomerRequest") === "CustomerRequest"
                ? [props.objectId]
                : []),
            ]),
          ],
          // Auftraege genauso, aber getrennt: das Lambda verknuepft sie mit eigenem Typ.
          orderIDs: [
            ...new Set([
              ...taskObjectIds(task, "Order"),
              ...(props.objectId && props.objectType === "Order" ? [props.objectId] : []),
            ]),
          ],
          attachments,
        },
      });

      // Der Datensatz kommt aus dem Sendepfad zurück. Kam KEINER, ist die Mail
      // trotzdem raus — das muss die Meldung sagen, sonst weiß der Nutzer nicht, ob es
      // geklappt hat. Der Sync holt sie in wenigen Minuten nach und dedupliziert.
      // Die Gegenrichtung der Aufgabe — dasselbe, was `logActivity` fuer Anruf,
      // Termin und Notiz tut. Vorher fehlte sie hier ganz: das Lambda schrieb
      // `Emails.taskID`, aber die Aufgabe blieb offen und ohne Zeiger, und die
      // Arbeitssitzung hakte sie nur in ihrem eigenen Zustand ab — nach einem Neuladen
      // stand sie wieder da. In `activityIDs` steht bei einer Mail-Aufgabe die
      // Emails-ID. Eine gesendete Mail erledigt die Aufgabe immer — anders als ein
      // Anruf, der niemanden erreicht (CallOutcomePanel).
      inFlight = false;
      await linkTaskActivity(task, response?.email?.id);

      if (response?.email) {
        toast.success(t("E-mail sent."));
      } else {
        toast.success(t("E-mail sent. It will appear in the history in a few minutes."));
      }
      if (typeof onSent === "function") onSent(response?.email ?? null);
      onClose();
    } catch (error) {
      console.error("Versand fehlgeschlagen", error);
      // 504 = Zeitlimit des Gateways; ein TypeError kommt aus fetch selbst, wenn die
      // Verbindung abreisst. In beiden Faellen kann die Mail beim Lambda angekommen sein.
      // Eine Antwort des Lambdas (jeder andere Status) ist dagegen eindeutig.
      if (inFlight && (error?.status === 504 || error instanceof TypeError)) {
        setSendUncertain(true);
        // Der Hinweis steht oben im Fenster — wer zum Senden nach unten gescrollt hat,
        // sieht ihn nicht. Der Toast ist der Teil, der sicher auffaellt.
        toast.warn(t("It is unclear whether the e-mail was sent. Please check before sending again."), {
          autoClose: 10000,
        });
        return;
      }
      const code = error?.code || error?.data?.code;
      // Nichts gesendet: der angekuendigte Verlauf geht nicht mehr. Den Hinweis auf den
      // Weg umstellen, der noch geht — der naechste Klick auf Senden bestaetigt ihn.
      if (code === "REPLY_QUOTE_UNAVAILABLE") {
        const details = error?.data?.details || {};
        setReplyPlan((current) => ({
          status: "ready",
          quoteMode: details.quoteMode,
          reason: details.reason,
          mailboxUpn: details.mailboxUpn,
          // Die Vorschau stimmt weiter, solange zitiert wird — der Text ist derselbe.
          quoteHtml: details.quoteMode === "none" ? null : current?.quoteHtml,
        }));
        toast.warn(
          t(
            "The conversation history cannot be included as shown. Nothing was sent. Check the notice below the text and send again."
          ),
          { autoClose: 8000 }
        );
        return;
      }
      let errorMessage;
      if (code === "M365_ACCESS_DENIED") {
        errorMessage = t(
          "Your Microsoft 365 account does not have permission to send emails. Please ensure your account is in the 'CRM Sales' group, or contact an administrator."
        );
      } else if (code === "NO_MAILBOX_CONFIGURED") {
        errorMessage = t(
          "No email address is configured for your user account. Please add an email address in your profile or contact an administrator."
        );
      } else if (code === "M365_MAILBOX_NOT_FOUND") {
        errorMessage = t(
          "The Microsoft 365 mailbox was not found or is not enabled for Exchange Online. Please contact an administrator."
        );
      } else if (code === "M365_CONFIG_ERROR") {
        errorMessage = t(
          "The Microsoft 365 integration is not configured or credentials have expired. Please contact an administrator."
        );
      } else if (code === "M365_THROTTLED") {
        errorMessage = t(
          "Microsoft 365 is temporarily overloaded. Please try again in a few minutes."
        );
      } else if (code === "INVALID_RECIPIENTS") {
        errorMessage = t(
          "One or more recipient addresses were rejected by Microsoft 365. Please verify the addresses."
        );
      } else if (code === "ATTACHMENT_ERROR") {
        errorMessage = t(
          "An attachment could not be uploaded to Microsoft 365. Please check the file size."
        );
      } else {
        errorMessage =
          error?.data?.message ||
          error?.message ||
          t("Could not send the e-mail.");
      }
      toast.error(errorMessage, { autoClose: 8000 });
    } finally {
      setSending(false);
    }
  };

  // Outlook-Anmutung ohne Editor-Bibliothek: Kopfzeilen als randlose Felder mit
  // Trennlinie, darunter eine Werkzeugzeile, darunter der Text ohne Rahmen.
  // ponytail: reines Bootstrap. Ceiling ist der fehlende Rich-Text — wer Fett und
  // Listen braucht, tauscht das Textfeld gegen einen Editor und setzt `isHtml: true`.
  const headerRow = "d-flex align-items-start border-bottom px-3";
  const headerLabel = "text-muted small py-2 flex-shrink-0";
  const headerLabelStyle = { width: 70 };
  const flatInput = "border-0 rounded-0 shadow-none px-0 py-2";

  const byType = (type) => templates.filter((item) => item.type === type);

  return (
    // `scrollable`: das Textfeld waechst mit dem Text, und das darf das Fenster nicht
    // ueber den Bildschirmrand schieben — gescrollt wird der Koerper, Kopf und Fuss
    // bleiben stehen. Wie im Mailprogramm.
    <Modal show onHide={onClose} centered size="lg" scrollable backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title className="h5">
          {replyTo ? t("Reply") : t("New e-mail")}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="p-0">
        {(blocked ||
          sendUncertain ||
          openGaps.length > 0 ||
          tooLarge ||
          badAddresses.length > 0) && (
          <div className="px-3 pt-3">
            {sendUncertain && (
              <Alert variant="warning" className="py-2">
                {t(
                  "It is unclear whether the e-mail was sent: the server did not answer in time, but may still be sending it. Check the history or your Sent Items in Outlook (it can take a few minutes) before sending again — otherwise the recipient may get it twice."
                )}
              </Alert>
            )}

            {blocked && (
              <Alert variant="warning" className="py-2">
                {t("This company is marked as 'do not contact'. Send anyway?")}
              </Alert>
            )}

            {/* C-II: ein unaufgeloester Platzhalter darf NIE beim Kunden landen.
                Deshalb drei Dinge zugleich: die Luecke steht SICHTBAR im Text an ihrer
                Stelle, hier stehen ihre Namen, und Senden bleibt gesperrt. */}
            {openGaps.length > 0 && (
              <Alert variant="danger" className="py-2">
                {t(
                  "{{count}} placeholder had no value. Fill in the gaps marked [[…]] in the text before sending: {{names}}",
                  { count: openGaps.length, names: openGaps.join(", ") }
                )}
              </Alert>
            )}

            {badAddresses.length > 0 && (
              <Alert variant="danger" className="py-2">
                {t("These addresses do not look like e-mail addresses: {{names}}", {
                  names: badAddresses.join(", "),
                })}
              </Alert>
            )}

            {tooLarge && (
              <Alert variant="danger" className="py-2">
                {t("Attachments are too large ({{size}}). The limit is {{limit}}.", {
                  size: formatBytes(totalBytes),
                  limit: formatBytes(MAX_ATTACHMENT_BYTES),
                })}
              </Alert>
            )}
          </div>
        )}

        {/* Der Absender steht da, ist aber KEIN Feld: das Postfach kommt im Lambda aus
            dem Token. Ein Auswahlfeld hier waere eine Luege. */}
        <div className={headerRow}>
          <span className={headerLabel} style={headerLabelStyle}>
            {t("From")}
          </span>
          <span
            className="py-2 text-truncate"
            title={t("Your mailbox — cannot be changed here.")}
          >
            {[user?.firstName, user?.lastName].filter(Boolean).join(" ")}
            {user?.email && (
              <span className="text-muted ms-1">&lt;{user.email}&gt;</span>
            )}
            <i className="mdi mdi-lock-outline text-muted ms-1" />
          </span>
        </div>

        <div className={headerRow}>
          <span className={headerLabel} style={headerLabelStyle}>
            {t("To")}
          </span>
          <div className="flex-grow-1">
            <AddressField
              id="compose-to"
              values={to}
              draft={toDraft}
              onChange={setTo}
              onDraftChange={setToDraft}
              placeholder="name@example.com"
            />
            {/* Die Firmenkontakte als Schnellauswahl statt als Pflichtfeld: eine Mail
                geht auch an jemanden, der noch kein Kontakt ist. */}
            {contacts.length > 0 && (
              <div className="d-flex flex-wrap gap-1 pb-2">
                {contacts
                  .filter((contact) => contact.email)
                  .map((contact) => (
                    <Button
                      key={contact.id}
                      size="sm"
                      variant="outline-secondary"
                      className="py-0 px-2"
                      onClick={() =>
                        setTo((current) =>
                          current.includes(contact.email)
                            ? current
                            : [...current, contact.email]
                        )
                      }
                    >
                      {[contact.firstName, contact.lastName]
                        .filter(Boolean)
                        .join(" ") || contact.email}
                    </Button>
                  ))}
              </div>
            )}
          </div>
          {/* Die Knoepfe stehen am rechten Ende der Empfaengerzeile und verschwinden,
              sobald ihre Zeile da ist — ein Knopf, der nichts mehr tut, ist Ballast. */}
          {(!showCc || !showBcc) && (
            <div className="d-flex flex-column flex-sm-row align-items-end align-items-sm-center gap-1 gap-sm-2 py-2 ps-2 flex-shrink-0">
              {!showCc && (
                <Button
                  variant="link"
                  size="sm"
                  className="p-0 text-decoration-none"
                  onClick={() => setShowCc(true)}
                >
                  + {t("Cc")}
                </Button>
              )}
              {!showBcc && (
                <Button
                  variant="link"
                  size="sm"
                  className="p-0 text-decoration-none"
                  onClick={() => setShowBcc(true)}
                >
                  + {t("Bcc")}
                </Button>
              )}
            </div>
          )}
        </div>

        {showCc && (
          <div className={headerRow}>
            <span className={headerLabel} style={headerLabelStyle}>
              {t("Cc")}
            </span>
            <AddressField
              id="compose-cc"
              values={cc}
              draft={ccDraft}
              onChange={setCc}
              onDraftChange={setCcDraft}
              placeholder="name@example.com"
            />
          </div>
        )}

        {showBcc && (
          <div className={headerRow}>
            <span className={headerLabel} style={headerLabelStyle}>
              {t("Bcc")}
            </span>
            <AddressField
              id="compose-bcc"
              values={bcc}
              draft={bccDraft}
              onChange={setBcc}
              onDraftChange={setBccDraft}
              placeholder="name@example.com"
            />
          </div>
        )}

        {/* Die Vorlage sitzt am Ende der Betreffzeile: sie ersetzt Betreff UND Text,
            gehoert also dorthin, wo der Betreff steht. */}
        <div className="d-flex align-items-center border-bottom px-3">
          <span className={headerLabel} style={headerLabelStyle}>
            {t("Subject")}
          </span>
          <Form.Control
            className={flatInput}
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
          />
          <Button
            variant={aiOpen ? "primary" : "outline-secondary"}
            size="sm"
            className="flex-shrink-0 ms-2"
            onClick={() => setAiOpen((prev) => !prev)}
            title={t("Write with AI")}
          >
            <i className="mdi mdi-auto-fix" />
          </Button>
          {byType("template").length > 0 && (
            <Dropdown align="end" className="flex-shrink-0 ms-2">
              <Dropdown.Toggle variant="outline-secondary" size="sm">
                {t("Template")}
                <span className="text-muted ms-1">({byType("template").length})</span>
              </Dropdown.Toggle>
              <Dropdown.Menu style={{ maxHeight: 320, overflowY: "auto" }}>
                {byType("template").map((item) => (
                  <Dropdown.Item
                    key={item.id}
                    onClick={() => insertTemplate(item)}
                    style={{ maxWidth: 380 }}
                  >
                    <div className="fw-semibold">{item.name}</div>
                    {item.subject && (
                      <div className="small text-truncate">{item.subject}</div>
                    )}
                    <div className="small text-muted text-truncate">
                      {previewOf(item.body) || t("(empty)")}
                    </div>
                  </Dropdown.Item>
                ))}
              </Dropdown.Menu>
            </Dropdown>
          )}
        </div>

        {/* Eine Zeile Anweisung — "was soll drin stehen". Alles andere (Empfaenger,
            Anrede, Sprache, Bezugsmail, Aufgabe) hat das Fenster schon und gibt es
            mit, ohne dass es jemand abtippt. */}
        {aiOpen && (
          <div className="d-flex align-items-center gap-2 px-3 py-2 border-bottom">
            <Form.Control
              size="sm"
              autoFocus
              value={aiPrompt}
              disabled={aiRunning}
              placeholder={t("What should the email say?")}
              onChange={(e) => setAiPrompt(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  writeWithAi();
                }
              }}
            />
            <Button
              size="sm"
              variant="primary"
              className="flex-shrink-0"
              disabled={aiRunning || !aiPrompt.trim()}
              onClick={writeWithAi}
            >
              {aiRunning && <BsSpinner animation="border" size="sm" className="me-1" />}
              {t("Write")}
            </Button>
            {aiUndo && !aiRunning && (
              <Button
                size="sm"
                variant="link"
                className="flex-shrink-0 p-0"
                onClick={undoAiDraft}
              >
                {t("Undo")}
              </Button>
            )}
          </div>
        )}

        {/* D-III Punkt 1: Liste mit Groesse und Entfernen. Bewusst kein
            DocumentsUpload: dort haengt ein S3-Upload dran, hier wird nichts
            gespeichert. */}
        {files.length > 0 && (
          <div className="px-3 py-2 border-bottom small">
            <span className="text-muted">
              {formatBytes(totalBytes)} / {formatBytes(MAX_ATTACHMENT_BYTES)}
            </span>
            <ul className="list-unstyled mb-0 mt-1">
              {files.map((file, index) => (
                <li
                  key={`${file.name}-${index}`}
                  className="d-flex align-items-center gap-2"
                >
                  <i className="mdi mdi-paperclip text-muted" />
                  <span>{file.name}</span>
                  <span className="text-muted">{formatBytes(file.size)}</span>
                  <Button
                    variant="link"
                    size="sm"
                    className="p-0 text-danger"
                    onClick={() =>
                      setFiles((current) => current.filter((_, i) => i !== index))
                    }
                  >
                    {t("Remove")}
                  </Button>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* EIN Bereich, wie im Mailprogramm: oben wird geschrieben, unten steht die
            Signatur — kein zweites Feld, das wie ein Formular aussieht. Der Bereich
            waechst mit dem Text, damit zwischen letzter Zeile und Signatur keine leere
            Flaeche klafft. */}
        <div style={{ minHeight: 260 }} className="position-relative">
          <div
            ref={bodyRef}
            contentEditable
            suppressContentEditableWarning
            role="textbox"
            aria-multiline="true"
            aria-label={t("Text")}
            className="px-3 pt-2 email-compose-body"
            style={{ minHeight: 180, outline: "none" }}
            onInput={() => {
              setBody(bodyRef.current.innerHTML);
              openMenuFromCaret();
            }}
            // Ist die Platzhalter-Liste offen, gehoert Tab ihr — auch ohne Treffer,
            // sonst rueckte der Absatz unter dem offenen Kasten ein.
            onKeyDown={(event) => {
              menuKeyDown(event);
              if (!menu) indentOnTab(event, setBody);
            }}
            onBlur={() => setMenu(null)}
            // Eingefuegtes HTML wird SOFORT gefiltert, nicht erst beim Senden: bis
            // dahin haengt es im DOM dieser Seite. Wie im Signatur-Editor. Die Schrift
            // der Quelle faellt dabei weg — der Text uebernimmt die des Textkoerpers.
            onPaste={(event) => {
              event.preventDefault();
              const clip = event.clipboardData;
              const pasted = clip.getData("text/html");
              exec(
                "insertHTML",
                pasted
                  ? stripFonts(sanitizeHtml(pasted))
                  : plainToHtml(plainLetters(clip.getData("text/plain")))
              );
            }}
          />

          {/* Ein contenteditable kennt kein `placeholder` — der Hinweis liegt darueber
              und laesst Klicks durch. */}
          {!htmlToPlain(body).trim() && !/<img/i.test(body) && (
            <div
              className="position-absolute text-muted px-3 pt-2"
              style={{ top: 0, left: 0, pointerEvents: "none" }}
            >
              {t("/ inserts a snippet, < inserts a placeholder")}
            </div>
          )}

          {/* Feste Position, weil die Cursorkoordinaten aus der Selection kommen —
              damit steht die Liste unter dem Cursor und nicht am Rand des Feldes. */}
          {menu && (
            <div
              className="position-fixed bg-white border rounded shadow-sm py-1"
              style={{
                left: menu.left,
                top: menu.top,
                minWidth: 280,
                maxWidth: 420,
                // Hohe Zeilen brauchen mehr Platz — reicht er nicht, wird gescrollt.
                maxHeight: "min(50vh, 320px)",
                overflowY: "auto",
                zIndex: 5,
              }}
            >
              {/* Ein leerer Kasten sagt nichts — er sieht aus wie ein Fehler. Wer noch
                  keinen Baustein hat, erfaehrt hier auch, wo er einen anlegt. */}
              {menuOptions.length === 0 && (
                <div className="dropdown-item-text small text-muted p-3">
                  <div>
                    {menu.trigger === "/"
                      ? t("No snippets found.")
                      : t("No placeholders found.")}
                  </div>
                  {menu.trigger === "/" &&
                    !templates.some((item) => item.type === "snippet") && (
                      <div className="mt-1">
                        {t(
                          "Create templates, snippets and signatures under Account."
                        )}
                      </div>
                    )}
                </div>
              )}
              {menuOptions.map((option, index) => (
                <button
                  type="button"
                  key={option.key}
                  // Zwei Zeilen und Luft dazwischen: bei einer Zeile pro Eintrag lag
                  // der naechste Platzhalter zu nah, und man klickte den falschen.
                  className={`dropdown-item small text-wrap py-2 ps-3 pe-3 ${
                    index === menu.index ? "active" : ""
                  }`}
                  // Beim Klick den Fokus im Textfeld lassen: sonst schliesst `onBlur`
                  // das Menue, bevor der Klick ankommt.
                  onMouseDown={(event) => {
                    event.preventDefault();
                    applyChoice(option);
                  }}
                  onMouseEnter={() =>
                    setMenu((current) => (current ? { ...current, index } : current))
                  }
                >
                  <div className="fw-semibold">{option.label}</div>
                  <div
                    className={`text-truncate ${
                      option.empty
                        ? "text-danger"
                        : index === menu.index
                        ? ""
                        : "text-muted"
                    }`}
                  >
                    {option.hint}
                  </div>
                </button>
              ))}
            </div>
          )}
          {/* Trennlinie mit der Auswahl an ihrem Anfang: sie sagt, wo der eigene Text
              endet, und die Auswahl steht dort, wo sie wirkt — vorher lag sie oben in
              der Werkzeugzeile, weit weg von dem, was sie aendert. */}
          {signatures.length > 0 && (
            <div className="d-flex align-items-center gap-2 px-3 pt-2">
              {/* Kurzes Stueck Linie links, damit die Trennung am Rand beginnt und
                  nicht erst hinter der Auswahl. */}
              <hr className="my-0 flex-shrink-0" style={{ width: 12 }} />
              <Form.Select
                size="sm"
                className="w-auto flex-shrink-0"
                value={signatureId}
                onChange={(e) => setSignatureId(e.target.value)}
              >
                <option value="">{t("No signature")}</option>
                {signatures.map((signature) => (
                  <option key={signature.id} value={signature.id}>
                    {signature.name}
                  </option>
                ))}
              </Form.Select>
              <hr className="flex-grow-1 my-0" />
            </div>
          )}

          {/* Die Signatur so, wie sie beim Empfaenger ankommt — im selben Bereich,
              direkt unter dem Text. Sie liegt bewusst nicht IM Textfeld: sonst muesste
              HTML als Zeichenkette hinein- und wieder herausgeschnitten werden. */}
          {signatureHtml && (
            <div
              className="px-3 pb-2 pt-1"
              // Anfassen soll man sie hier nicht — sie kommt aus der Signaturliste,
              // und ein halb geaenderter HTML-Block waere nicht speicherbar.
              title={t("Change or remove the signature above the divider.")}
            >
              {/* Abgeschottet statt im Seiten-DOM: eine Outlook-Signatur bringt ihre
                  eigene Schrift und Abstaende mit, und im Fenster wuerde Bootstrap sie
                  ueberschreiben — die Vorschau zeigte dann etwas anderes als die
                  gesendete Mail. */}
              <SignaturePreview html={signatureHtml} title={t("Signature")} />
            </div>
          )}
        </div>

        {/* #4264: was unter dem Text beim Empfaenger ankommt. Das Zitat setzt erst das
            Lambda ein — hier steht, OB und WIE, und auf Wunsch die Vorschau. */}
        {replyPlan && (
          <ReplyHistoryNotice
            plan={replyPlan}
            showQuote={showQuote}
            onToggleQuote={() => setShowQuote((open) => !open)}
          />
        )}
      </Modal.Body>
      {/* Zwei Reihen: oben die Bearbeitung, unten Firmenwechsel und Senden. In einer
          Reihe rutschte „Senden" beim Umbrechen neben die Formatknoepfe. */}
      <Modal.Footer className="flex-column align-items-stretch gap-2">
        <div className="d-flex align-items-center gap-1 flex-wrap">
        {/* Formatierung im Fuss, wie im Mailprogramm: die Knoepfe stehen bei „Senden"
            und nicht zwischen Betreff und Text. Dieselbe Werkzeugzeile wie im
            Vorlagen-Editor — `EmailFormatToolbar` haelt Fett/Kursiv/Unterstrichen,
            Ueberschrift und Link. */}
        <EmailFormatToolbar fieldRef={bodyRef} onChange={setBody} />

        <Button
          variant="link"
          size="sm"
          className="text-body px-1"
          title={t("Picture")}
          aria-label={t("Picture")}
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => imageRef.current?.click()}
        >
          <i className="mdi mdi-image-outline" />
        </Button>
        {/* Bild IM Text: als data:-URI eingebettet. Das Lambda macht daraus einen
            cid-Anhang, genau wie beim Signaturlogo — deshalb genuegt hier das `img`. */}
        <Form.Control
          ref={imageRef}
          type="file"
          accept="image/*"
          className="d-none"
          onChange={async (event) => {
            const file = event.target.files?.[0];
            event.target.value = "";
            if (!file) return;
            try {
              const dataUrl = await readDataUrl(file);
              exec(
                "insertHTML",
                `<img src="${dataUrl}" alt="${file.name.replace(/"/g, "")}" style="max-width:100%">`
              );
            } catch (error) {
              toast.error(error?.message || t("Could not load the attachment."));
            }
          }}
        />

        <Button
          variant="link"
          size="sm"
          className="text-body px-1"
          title={t("Attach")}
          aria-label={t("Attach")}
          onClick={() => fileRef.current?.click()}
        >
          <i className="mdi mdi-paperclip" />
        </Button>
        <Form.Control
          ref={fileRef}
          type="file"
          multiple
          className="d-none"
          onChange={(e) => {
            addFiles(e.target.files);
            // Zuruecksetzen, damit dieselbe Datei erneut waehlbar bleibt. Muss NACH
            // addFiles stehen, das dort die Liste bereits kopiert hat.
            e.target.value = "";
          }}
        />

        {/* Wer noch keine hat, muss nicht raten, wo sie entstehen. */}
        {templates.length === 0 && signatures.length === 0 && (
          <Link to="/account" className="small text-muted ms-2">
            {t("Create templates, snippets and signatures under Account.")}
          </Link>
        )}
        </div>

        <div className="d-flex align-items-center gap-2">
        {onChangeCompany && (
          <Button
            variant="light"
            className="d-inline-flex align-items-center gap-1"
            onClick={onChangeCompany}
          >
            <i className="uil-suitcase" />
            {t("Choose a different company")}
          </Button>
        )}

        <Button
          className="ms-auto"
          variant={blocked || sendUncertain ? "warning" : "primary"}
          onClick={send}
          disabled={
            sending ||
            tooLarge ||
            badAddresses.length > 0 ||
            openGaps.length > 0 ||
            !toAddresses.length
          }
        >
          {sending ? (
            <BsSpinner size="sm" animation="border" className="me-1" />
          ) : (
            <i className="mdi mdi-send me-1" />
          )}
          {sendUncertain
            ? t("Send again anyway")
            : blocked
            ? t("Send anyway")
            : t("Send")}
        </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default EmailComposeModal;
